import {
  getGameActive,
  type LineupEntry,
  type TeamSide,
} from "@aura-frontend/omnibus-az-client";
import { useParams } from "react-router-dom";

import { getHomeAwayLineups } from "./api-helpers";

/**
 * The lineup's own entry for the player `rebuilt` describes (same player, same
 * position), else `rebuilt`.
 */
export const findSavedLineupEntry = (
  lineup: LineupEntry[],
  rebuilt: LineupEntry,
): LineupEntry =>
  lineup.find(
    (entry) =>
      entry.externalId === rebuilt.externalId &&
      entry.position === rebuilt.position,
  ) ?? rebuilt;

/**
 * A substitution's outgoing player exactly as the active game's lineup stores
 * them. Omnibus only substitutes out a player matching their entry field for
 * field, and other writers (Scorekeeping V2, other apps) can store a player
 * differently from how this tagger rebuilds them, e.g. "First Last" names.
 * Falls back to `rebuilt` if the game can't be read.
 */
const fetchSavedLineupEntry = async (
  machineUuid: string,
  side: TeamSide,
  rebuilt: LineupEntry,
): Promise<LineupEntry> => {
  try {
    const { data } = await getGameActive({
      query: { machineUuid },
      throwOnError: true,
    });
    const lineups = getHomeAwayLineups(data.game);
    const lineup = side === "home" ? lineups.home : lineups.away;
    return findSavedLineupEntry(lineup, rebuilt);
  } catch {
    return rebuilt;
  }
};

/** fetchSavedLineupEntry for this page's SmartPark. */
export const useFetchSavedLineupEntry = () => {
  const { id: machineUuid } = useParams() as { id: string };
  return (side: TeamSide, rebuilt: LineupEntry) =>
    fetchSavedLineupEntry(machineUuid, side, rebuilt);
};
